import { useContext, useEffect, useState } from 'react';
import { Modal, ModalDialog, ModalOverflow, Stack, Typography, FormControl, FormLabel, Input, Button, Textarea, Chip, Autocomplete, Box, IconButton, Sheet, ModalClose, ChipDelete } from '@mui/joy';
import { motion, AnimatePresence } from 'framer-motion';
import { LayoutContext } from '../../context';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import MonochromePhotosRoundedIcon from '@mui/icons-material/MonochromePhotosRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import { APICreateCommunity, APIGetPopularTags, APIUpdateCommunity, APIGetCommunityDetail, APIGetChats, API_URL } from '../../api';
import classes from './ModalCommunity.module.css';


export default function ModalCommunity({ open, onClose, onCommunityCreated, onCommunityUpdated, communityId = null, mode = 'create' }) {
    const { deviceType, token, setChats } = useContext(LayoutContext);
    
    const [formData, setFormData] = useState({
        name: '',
        city: '',
        description: '',
        tags: [],
        directions: [],
        theme: '',
        region: '',
        photo: null
    });

    const [availableTags, setAvailableTags] = useState([]);
    const [availableSuggestions, setAvailableSuggestions] = useState({
        themes: [],
        directions: [],
        regions: [],
        cities: []
    });
    const [loadingSuggestions, setLoadingSuggestions] = useState(false);
    const [loadingTags, setLoadingTags] = useState(false);
    const [saving, setSaving] = useState(false);
    const [loadingCommunity, setLoadingCommunity] = useState(false);
    const [tagInput, setTagInput] = useState('');
    const [directionInput, setDirectionInput] = useState('');
    const [photoPreview, setPhotoPreview] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);

    // Загрузка популярных тегов и подсказок при открытии
    useEffect(() => {
        const loadData = async () => {
            if (open && token) {
                setLoadingTags(true);
                setLoadingSuggestions(true);
                try {
                    // Загружаем популярные теги
                    const tagsData = await APIGetPopularTags(token);
                    const tags = tagsData.popular_tags?.map(item => item.tag) || [];
                    setAvailableTags(tags);

                    // Загружаем подсказки для всех полей
                    const suggestionsData = await APIGetAllCommunitySuggestions(token);
                    setAvailableSuggestions(suggestionsData);
                } catch (error) {
                    console.error('Ошибка загрузки данных:', error);
                    // Запасные варианты
                    setAvailableTags(['путешествия', 'спорт', 'искусство', 'бизнес', 'технологии', 'музыка', 'кино', 'книги']);
                    setAvailableSuggestions({
                        themes: ['IT', 'Бизнес', 'Творчество', 'Спорт', 'Образование'],
                        directions: ['Программирование', 'Дизайн', 'Маркетинг', 'Фотография', 'Музыка'],
                        regions: ['Московская область', 'Ленинградская область', 'Центральный регион'],
                        cities: ['Москва', 'Санкт-Петербург', 'Новосибирск']
                    });
                } finally {
                    setLoadingTags(false);
                    setLoadingSuggestions(false);
                }
            }
        };
        
        loadData();
    }, [open, token]);

    // Загрузка данных сообщества для редактирования
    useEffect(() => {
        const loadCommunityData = async () => {
            if (open && mode === 'edit' && communityId && token) {
                setLoadingCommunity(true);
                try {
                    const communityData = await APIGetCommunityDetail(token, communityId);
                    
                    setFormData({
                        name: communityData.name || '',
                        city: communityData.city || '',
                        description: communityData.description || '',
                        tags: communityData.tag_list || [],
                        directions: communityData.direction_list || [],
                        theme: communityData.theme || '',
                        region: communityData.region || '',
                        photo: null
                    });
                    
                    // Устанавливаем превью фото если есть
                    if (communityData.photo_url) {
                        setPhotoPreview(`${API_URL}${communityData.photo_url}`);
                    }
                    
                } catch (error) {
                    console.error('Ошибка загрузки данных сообщества:', error);
                    alert('Не удалось загрузить данные сообщества');
                } finally {
                    setLoadingCommunity(false);
                }
            }
        };

        if (open && mode === 'edit') {
            loadCommunityData();
        } else if (open && mode === 'create') {
            // Сброс формы при создании
            resetForm();
        }
    }, [open, mode, communityId, token]);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleAddTag = (newValue) => {
        if (newValue && !formData.tags.includes(newValue)) {
            setFormData(prev => ({
                ...prev,
                tags: [...prev.tags, newValue]
            }));
        }
        setTagInput('');
    };

    const handleRemoveTag = (tagToRemove) => {
        console.log("fff", tagToRemove);
        setFormData(prev => ({
            ...prev,
            tags: prev.tags.filter(tag => tag !== tagToRemove)
        }));
    };

    const handleAddDirection = (newValue) => {
        if (newValue && !formData.directions.includes(newValue)) {
            setFormData(prev => ({
                ...prev,
                directions: [...prev.directions, newValue]
            }));
        }
        setDirectionInput('');
    };

    const handleRemoveDirection = (directionToRemove) => {
        setFormData(prev => ({
            ...prev,
            directions: prev.directions.filter(direction => direction !== directionToRemove)
        }));
    };

    const handleTagInputChange = (event, value) => {
        setTagInput(value);
    };

    const handleDirectionInputChange = (event, value) => {
        setDirectionInput(value);
    };
    
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        
        if (!file) return;

        // Базовая проверка типа файла
        if (!file.type.startsWith('image/')) {
            alert('Пожалуйста, выберите файл изображения');
            e.target.value = '';
            return;
        }

        // Проверка размера файла
        const maxSize = 5 * 1024 * 1024; // 5MB
        if (file.size > maxSize) {
            alert('Размер файла слишком большой. Максимальный размер: 5MB');
            e.target.value = '';
            return;
        }

        console.log('Файл выбран:', file.name, file.size, file.type);
        setSelectedFile(file);
        
        // Создаем превью
        const reader = new FileReader();
        reader.onload = (e) => {
            console.log('Превью создано');
            setPhotoPreview(e.target.result);
        };
        reader.readAsDataURL(file);
        
        e.target.value = ''; // Сбрасываем input
    };

    const handleRemovePhoto = () => {
        setSelectedFile(null);
        setPhotoPreview(null);
    };

    const handleSaveCommunity = async () => {
        // Валидация формы
        if (!formData.name.trim()) {
            alert('Пожалуйста, введите название сообщества');
            return;
        }

        if (!formData.city.trim()) {
            alert('Пожалуйста, укажите город');
            return;
        }

        setSaving(true);
        try {
            // Подготавливаем данные для отправки
            const requestData = {
                name: formData.name.trim(),
                city: formData.city.trim(),
                description: formData.description.trim() || '',
                tag_list: formData.tags || [],
                direction_list: formData.directions || [],
                theme: formData.theme.trim() || '',
                region: formData.region.trim() || '',
                photo_default: photoPreview ? true : false
            };

            console.log('Подготовленные данные:', requestData);

            // Создаем FormData
            const finalData = new FormData();
            
            // Добавляем текстовые поля
            Object.keys(requestData).forEach(key => {
                if (key === 'tag_list' || key === 'direction_list') {
                    // Для массивов добавляем каждый элемент отдельно
                    requestData[key].forEach(item => {
                        finalData.append(key, item);
                    });
                } else {
                    finalData.append(key, requestData[key]);
                }
            });
            
            // Добавляем файл если есть
            if (selectedFile) {
                finalData.append('photo', selectedFile);
            }

            console.log('Финальные данные для отправки:', finalData);

            let response;

            // Выполняем запрос в зависимости от режима
            if (mode === 'create') {
                response = await APICreateCommunity(token, finalData);

                if (response.community_id) {
                    onCommunityCreated(response.community_id);
                    resetForm();
                    onClose();
                } else {
                    throw new Error('Сервер не вернул ID созданного сообщества');
                }

            } else if (mode === 'edit' && communityId) {
                response = await APIUpdateCommunity(token, communityId, finalData);

                if (response.success) {
                    onCommunityUpdated(communityId, response.community);
                    resetForm();
                    onClose();
                } else {
                    throw new Error('Сервер не подтвердил успешное обновление');
                }
            }
            
            const chat_data = await APIGetChats(token);
            setChats(chat_data?.chats);
        } catch (error) {
            console.error(`Ошибка ${mode === 'create' ? 'создания' : 'обновления'} сообщества:`, error);
            alert(error.response?.data?.error || `Произошла ошибка при ${mode === 'create' ? 'создании' : 'обновлении'} сообщества`);
        } finally {
            setSaving(false);
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            city: '',
            description: '',
            tags: [],
            directions: [],
            theme: '',
            region: '',
            photo: null
        });
        setTagInput('');
        setDirectionInput('');
        setSelectedFile(null);
        setPhotoPreview(null);
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const isFormValid = formData.name.trim() && formData.city.trim();
    const isEditing = mode === 'edit';

    return (
        <AnimatePresence>
            {open && (
                <Modal
                    sx={{ zIndex: 10022 }}
                    open={open}
                    onClose={handleClose}
                    slotProps={{
                        backdrop: {
                            component: motion.div,
                            initial: { opacity: 0, backdropFilter: 'blur(0px)' },
                            animate: { opacity: 1, backdropFilter: 'blur(8px)' },
                            exit: { opacity: 0, backdropFilter: 'blur(0px)' },
                            transition: { duration: 0.3, ease: "easeInOut" },
                        }
                    }}
                >
                    <ModalOverflow sx={{ height: 'auto' }}>
                        <ModalDialog
                            layout="fullscreen"
                            size="lg"
                            component={motion.div}
                            initial={{ opacity: 0, y: -10, scale: 0.98 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 10, scale: 0.98 }}
                            transition={{ duration: 0.2, ease: "easeInOut" }}
                            sx={(theme) => ({
                                [theme.breakpoints.up("xl")]: { width: '500px', mx: 0 },
                                [theme.breakpoints.down("xl")]: { width: '500px', mx: 0 },
                                [theme.breakpoints.down("lg")]: { width: '500px', mx: 0 },
                                [theme.breakpoints.down("md")]: { width: '400px', mx: 0 },
                                [theme.breakpoints.down("sm")]: { width: '95%', p: 3 },
                                width: '100%'
                            })}
                        >
                            <Box sx={{ mt: deviceType == 'Phone' ? 10 : 0 }}></Box>
                            <ModalClose sx={{ mt: deviceType == 'Phone' ? 10 : 0 }} onClick={handleClose}/>
                            
                            <Stack direction={"column"} alignItems={'center'} spacing={2} sx={{ mt: 2, mb: 1 }}>
                                <GroupsRoundedIcon sx={{ fontSize: 40, color: 'primary.500' }} />
                                <Typography level="h3" sx={{ textAlign: 'center' }}>
                                    {isEditing ? 'Редактировать сообщество' : 'Создать сообщество'}
                                </Typography>
                                <Typography level="body-sm" sx={{ textAlign: 'center', color: 'neutral.600' }}>
                                    {isEditing 
                                        ? 'Измените информацию о вашем сообществе' 
                                        : 'Создайте сообщество по интересам и приглашайте участников'
                                    }
                                </Typography>
                            </Stack>
                            
                            <Stack spacing={3} sx={{ width: '100%', maxWidth: 400, mx: 'auto', mt: 3 }}>
                                {/* Фото сообщества */}
                                <FormControl>
                                    <FormLabel>Фото сообщества</FormLabel>
                                    <Box
                                        sx={{ 
                                            border: "2px dashed #ccc", 
                                            borderRadius: 'lg', 
                                            p: 2, 
                                            textAlign: "center"
                                        }}
                                    >
                                        {photoPreview ? (
                                            <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                                <Box
                                                    component={"img"} 
                                                    src={photoPreview} 
                                                    alt="Preview" 
                                                    style={{ 
                                                        width: 150, 
                                                        height: 150, 
                                                        objectFit: 'cover', 
                                                        borderRadius: 'var(--joy-radius-lg)' 
                                                    }} 
                                                />
                                                <IconButton
                                                    onClick={handleRemovePhoto}
                                                    sx={{ 
                                                        position: 'absolute', 
                                                        top: -10, 
                                                        right: -10,
                                                    }}
                                                    color="danger"
                                                    variant="soft"
                                                    size="sm"
                                                >
                                                    <RemoveRoundedIcon />
                                                </IconButton>
                                                {selectedFile && (
                                                    <Typography level="body-xs" sx={{ 
                                                        position: 'absolute', 
                                                        bottom: 5, 
                                                        left: 5, 
                                                        backgroundColor: 'rgba(0,0,0,0.7)', 
                                                        color: 'white', 
                                                        px: 1, 
                                                        borderRadius: 'sm',
                                                        fontSize: '0.7rem'
                                                    }}>
                                                        {(selectedFile.size / 1024 / 1024).toFixed(1)}MB
                                                    </Typography>
                                                )}
                                            </Box>
                                        ) : (
                                            <Stack direction={'column'} spacing={1} sx={{ my: 1 }}>
                                                <Box className={classes.file_upload_container}>
                                                    <input
                                                        type="file"
                                                        id="communityPhotoInput"
                                                        accept="image/*"
                                                        onChange={handleFileChange}
                                                    />
                                                    <label htmlFor="communityPhotoInput" className={classes.custom_file_button}>
                                                        <MonochromePhotosRoundedIcon color="primary" sx={{ fontSize: '20px !important' }} /> 
                                                        {isEditing ? 'Изменить фото' : 'Выбрать фото'}
                                                    </label>
                                                </Box>
                                                <Typography level="title-sm" sx={{ color: "neutral.400" }}>
                                                    Перетащите файл сюда
                                                </Typography>
                                            </Stack>
                                        )}
                                    </Box>
                                    <Typography level="body-xs" sx={{ color: 'neutral.500', mt: 0.5 }}>
                                        Необязательное поле
                                    </Typography>
                                </FormControl>

                                {/* Название сообщества */}
                                <FormControl required>
                                    <FormLabel>Название сообщества</FormLabel>
                                    <Input
                                        value={formData.name}
                                        onChange={(e) => handleInputChange('name', e.target.value)}
                                        placeholder="Введите название сообщества"
                                        sx={{ borderRadius: 'lg' }}
                                        disabled={loadingCommunity}
                                    />
                                </FormControl>

                                {/* Тема сообщества */}
                                <FormControl>
                                    <FormLabel>Тема сообщества</FormLabel>
                                    <Autocomplete
                                        freeSolo
                                        options={availableSuggestions.themes}
                                        value={formData.theme}
                                        onInputChange={(event, value) => handleInputChange('theme', value)}
                                        placeholder="Выберите или введите тему"
                                        loading={loadingSuggestions}
                                        disabled={loadingCommunity}
                                        slotProps={{ listbox: { sx: { zIndex: 100010 } } }}
                                        renderInput={(params) => <Input {...params} />}
                                    />
                                    <Typography level="body-xs" sx={{ color: 'neutral.500', mt: 0.5 }}>
                                        Необязательное поле
                                    </Typography>
                                </FormControl>

                                {/* Город */}
                                <FormControl required>
                                    <FormLabel>Город</FormLabel>
                                    <Input
                                        value={formData.city}
                                        onChange={(e) => handleInputChange('city', e.target.value)}
                                        placeholder="В каком городе находится сообщество?"
                                        sx={{ borderRadius: 'lg' }}
                                        disabled={loadingCommunity}
                                    />
                                </FormControl>

                                {/* Регион */}
                                <FormControl>
                                    <FormLabel>Регион</FormLabel>
                                    <Autocomplete
                                        freeSolo
                                        options={availableSuggestions.regions}
                                        value={formData.region}
                                        onInputChange={(event, value) => handleInputChange('region', value)}
                                        placeholder="Выберите или введите регион"
                                        loading={loadingSuggestions}
                                        disabled={loadingCommunity}
                                        slotProps={{ listbox: { sx: { zIndex: 100010 } } }}
                                        renderInput={(params) => <Input {...params} />}
                                    />
                                    <Typography level="body-xs" sx={{ color: 'neutral.500', mt: 0.5 }}>
                                        Необязательное поле
                                    </Typography>
                                </FormControl>

                                {/* Направления сообщества */}
                                <FormControl>
                                    <FormLabel>Направления сообщества</FormLabel>
                                    <Stack spacing={1}>
                                        <Autocomplete
                                            freeSolo
                                            options={availableSuggestions.directions}
                                            value={directionInput}
                                            inputValue={directionInput}
                                            onInputChange={handleDirectionInputChange}
                                            onChange={(event, newValue) => {
                                                if (newValue) {
                                                    handleAddDirection(newValue);
                                                }
                                            }}
                                            placeholder={loadingSuggestions ? "Загрузка направлений..." : "Добавьте направления"}
                                            loading={loadingSuggestions}
                                            disabled={loadingCommunity}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' && directionInput.trim()) {
                                                    e.preventDefault();
                                                    handleAddDirection(directionInput.trim());
                                                }
                                            }}
                                            slotProps={{ listbox: { sx: { zIndex: 100010 } } }}
                                            renderInput={(params) => (
                                                <Input
                                                    {...params}
                                                    endDecorator={
                                                        directionInput.trim() && (
                                                            <IconButton
                                                                size="sm"
                                                                onClick={() => handleAddDirection(directionInput.trim())}
                                                                sx={{ mr: -0.5 }}
                                                            >
                                                                <AddRoundedIcon />
                                                            </IconButton>
                                                        )
                                                    }
                                                />
                                            )}
                                        />
                                        
                                        {/* Выбранные направления */}
                                        {formData.directions.length > 0 && (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                {formData.directions.map((direction, index) => (
                                                    <Chip
                                                        key={index}
                                                        variant="soft"
                                                        color="primary"
                                                        endDecorator={<ChipDelete onDelete={() => handleRemoveDirection(direction)} />}
                                                    >
                                                        {direction}
                                                    </Chip>
                                                ))}
                                            </Box>
                                        )}
                                        
                                        <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                                            Начните вводить направление и нажмите Enter или выберите из списка
                                        </Typography>
                                    </Stack>
                                </FormControl>
                                
                                {/* Описание */}
                                <FormControl>
                                    <FormLabel>Описание сообщества</FormLabel>
                                    <Textarea
                                        value={formData.description}
                                        onChange={(e) => handleInputChange('description', e.target.value)}
                                        placeholder="Расскажите о вашем сообществе, его целях и интересах..."
                                        minRows={3}
                                        sx={{ borderRadius: 'lg' }}
                                        disabled={loadingCommunity}
                                    />
                                    <Typography level="body-xs" sx={{ color: 'neutral.500', mt: 0.5 }}>
                                        Необязательное поле
                                    </Typography>
                                </FormControl>

                                {/* Теги */}
                                <FormControl>
                                    <FormLabel>Теги сообщества</FormLabel>
                                    <Stack spacing={1}>
                                        <Autocomplete
                                            freeSolo
                                            options={availableTags}
                                            value={tagInput}
                                            inputValue={tagInput}
                                            onInputChange={handleTagInputChange}
                                            onChange={(event, newValue) => {
                                                if (newValue) {
                                                    handleAddTag(newValue);
                                                }
                                            }}
                                            placeholder={loadingTags ? "Загрузка тегов..." : "Добавьте теги для поиска"}
                                            loading={loadingTags}
                                            disabled={loadingCommunity}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' && tagInput.trim()) {
                                                    e.preventDefault();
                                                    handleAddTag(tagInput.trim());
                                                }
                                            }}
                                            renderInput={(params) => (
                                                <Input
                                                    {...params}
                                                    endDecorator={
                                                        tagInput.trim() && (
                                                            <IconButton
                                                                size="sm"
                                                                onClick={() => handleAddTag(tagInput.trim())}
                                                                sx={{ mr: -0.5 }}
                                                            >
                                                                <AddRoundedIcon />
                                                            </IconButton>
                                                        )
                                                    }
                                                />
                                            )}
                                        />
                                        
                                        {/* Выбранные теги */}
                                        {formData.tags.length > 0 && (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                {formData.tags.map((tag, index) => (
                                                    <Chip
                                                        key={index}
                                                        variant="soft"
                                                        color="primary"
                                                        endDecorator={<ChipDelete onDelete={() => handleRemoveTag(tag)} />}
                                                        // endDecorator={
                                                        //     <CloseRoundedIcon 
                                                        //         sx={{ fontSize: '16px' }}
                                                        //         onClick={() => handleRemoveTag(tag)}
                                                        //     />
                                                        // }
                                                    >
                                                        #{tag}
                                                    </Chip>
                                                ))}
                                            </Box>
                                        )}
                                        
                                        <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                                            Начните вводить тег и нажмите Enter или выберите из списка
                                        </Typography>
                                    </Stack>
                                </FormControl>

                                {/* Подсказки по тегам */}
                                {availableTags.length > 0 && (
                                    <Sheet variant="soft" sx={{ p: 2, borderRadius: 'lg' }}>
                                        <Typography level="body-sm" sx={{ mb: 1, fontWeight: 'bold' }}>
                                            Популярные теги:
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                            {availableTags.slice(0, 8).map((tag, index) => (
                                                <Chip
                                                    key={index}
                                                    size="sm"
                                                    variant="outlined"
                                                    onClick={() => handleAddTag(tag)}
                                                    sx={{ cursor: 'pointer' }}
                                                >
                                                    #{tag}
                                                </Chip>
                                            ))}
                                        </Box>
                                    </Sheet>
                                )}

                                {/* Кнопка сохранения */}
                                <Button
                                    size="lg"
                                    loading={saving || loadingCommunity}
                                    onClick={handleSaveCommunity}
                                    disabled={!isFormValid || saving || loadingCommunity}
                                    sx={{ mt: 2, borderRadius: 'lg' }}
                                >
                                    {isEditing ? 'Сохранить изменения' : 'Создать сообщество'}
                                </Button>

                                {/* Подсказка */}
                                <Typography level="body-xs" sx={{ textAlign: 'center', color: 'neutral.500' }}>
                                    {isEditing 
                                        ? 'Изменения будут видны всем участникам сообщества'
                                        : 'После создания вы станете администратором сообщества и сможете приглашать участников'
                                    }
                                </Typography>
                            </Stack>
                        </ModalDialog>
                    </ModalOverflow>
                </Modal>
            )}
        </AnimatePresence>
    );
}